import { OrderUnitOfMeasure } from '@treadinc/horizon-api-spec';

import { DeliveredQuantity } from '~hooks/useOrders/models';
import { ItemNameAndId } from '~types/ItemNameAndId';

/**
 * Weight/Volume UOMs - compare against job's expected UOM.
 * These represent material quantities where the job specifies what unit to use.
 */
const WEIGHT_VOLUME_UOMS: Set<string> = new Set([
  OrderUnitOfMeasure.TON.toLowerCase(),
  OrderUnitOfMeasure.TONNE.toLowerCase(),
  OrderUnitOfMeasure.YARD.toLowerCase(),
  OrderUnitOfMeasure.CUBIC_METER.toLowerCase(),
  OrderUnitOfMeasure.LITER.toLowerCase(),
  OrderUnitOfMeasure.GALLON.toLowerCase(),
  OrderUnitOfMeasure.BARREL.toLowerCase(),
  OrderUnitOfMeasure.BUSHEL.toLowerCase(),
  OrderUnitOfMeasure.BAG.toLowerCase(),
  OrderUnitOfMeasure.PALLET.toLowerCase(),
  OrderUnitOfMeasure.POUND.toLowerCase(),
  OrderUnitOfMeasure.EACH.toLowerCase(),
]);

/**
 * Check if a UOM is a weight/volume type.
 * Weight/volume UOMs should be compared against the job's expected UOM.
 * Non-weight/volume UOMs (Hour, Load, Mile, etc.) only need loads to match each other.
 */
function isWeightVolumeUom(uom: string | null | undefined): boolean {
  if (!uom) return false;
  return WEIGHT_VOLUME_UOMS.has(uom.toLowerCase());
}

/**
 * Normalize UOM string for case-insensitive comparison.
 * "Ton" and "TON" are treated as equal, but "Ton" and "TONNE" are different.
 */
function normalizeUom(uom: string | null | undefined): string {
  return (uom ?? '').toLowerCase();
}

/**
 * Compare two UOMs (case-insensitive).
 * Returns true if they are different units.
 */
function hasUomMismatch(
  expectedUom: string | OrderUnitOfMeasure | null | undefined,
  actualUom: string | OrderUnitOfMeasure | null | undefined,
): boolean {
  if (!expectedUom || !actualUom) return false;
  return normalizeUom(expectedUom) !== normalizeUom(actualUom);
}

/**
 * Get the expected UOM for a job, following priority rules:
 * 1. Equipment Capacity UOM (if present)
 * 2. Job's load UOM (fallback)
 * 3. Job's main UOM (final fallback)
 */
export function getExpectedJobUom(
  equipmentCapacityUom: ItemNameAndId | null | undefined,
  jobLoadUom: ItemNameAndId | null | undefined,
  jobUom?: ItemNameAndId | null | undefined,
): ItemNameAndId | null {
  return equipmentCapacityUom ?? jobLoadUom ?? jobUom ?? null;
}

/**
 * Check if any delivered quantities in a job have a UOM mismatch.
 * Used to show caution flag on job row.
 *
 * Logic:
 * - For weight/volume UOMs: Compare each load's UOM against the job's expected UOM
 * - For non-weight/volume UOMs (Hour, Load, etc.): Check if all loads have consistent UOMs
 */
export function jobHasAnyUomMismatch(
  equipmentCapacityUom: ItemNameAndId | null | undefined,
  jobLoadUom: ItemNameAndId | null | undefined,
  deliveredQuantities: DeliveredQuantity[],
  jobUom?: ItemNameAndId | null | undefined,
): boolean {
  if (deliveredQuantities.length === 0) return false;

  const expectedUom = getExpectedJobUom(equipmentCapacityUom, jobLoadUom, jobUom);

  // Determine which comparison mode to use based on expected UOM
  if (expectedUom && isWeightVolumeUom(expectedUom.id)) {
    // Weight/volume: compare each load against job's expected UOM
    return deliveredQuantities.some((dq) =>
      hasUomMismatch(expectedUom.id, dq.unitOfMeasure),
    );
  }

  // Non-weight/volume (Hour, Load, Mile, etc.): check loads match each other
  if (deliveredQuantities.length < 2) return false;

  const firstUom = deliveredQuantities[0].unitOfMeasure;
  return deliveredQuantities
    .slice(1)
    .some((dq) => hasUomMismatch(firstUom, dq.unitOfMeasure));
}

/**
 * Check if a specific load has a UOM mismatch.
 * Used to show caution flag on individual load cards.
 *
 * For weight/volume UOMs: Compare against job's expected UOM.
 * For non-weight/volume UOMs: This function is not applicable (use jobHasAnyUomMismatch).
 */
export function loadHasUomMismatch(
  equipmentCapacityUom: ItemNameAndId | null | undefined,
  jobLoadUom: ItemNameAndId | null | undefined,
  loadUom: ItemNameAndId | null | undefined,
  jobUom?: ItemNameAndId | null | undefined,
): boolean {
  if (!loadUom?.id) return false;

  const expectedUom = getExpectedJobUom(equipmentCapacityUom, jobLoadUom, jobUom);
  if (!expectedUom?.id) return false;

  // Only flag weight/volume UOMs at the load level
  if (!isWeightVolumeUom(expectedUom.id)) return false;

  return hasUomMismatch(expectedUom.id, loadUom.id);
}
